import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Mic, Video as VideoIcon } from '../../icons';
import { requestPresignedDownload } from '../../api/files';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../../lib/shared-i18n';
import type { MessageAttachment } from '../../types/api';

/**
 * AttachmentMedia (chat-inline-media-playback — 2026-07-12) — 오디오/비디오 첨부 인라인 재생.
 *
 * additive: AttachmentList 의 image/file 분기에 새 분기(audio/video)로만 추가한다. 기존 컴포넌트
 * (AttachmentImage/AttachmentFile) 는 무변경. 미지원/실패 시 fallback(파일 카드)으로 강등해
 * 다운로드 경로를 보존한다(무회귀).
 *
 * - presigned GET(disposition=inline) 으로 MinIO URL 확보 → <audio>/<video controls preload=metadata>.
 *   MinIO 는 Range(206) 네이티브 → 브라우저가 프로그레시브 스트리밍·seek 를 알아서 한다.
 * - presign TTL 5분(재생 중 만료 → seek 시 403) 대비: 미디어 error 시 1회 URL 재발급(fresh 서명)
 *   후 src 교체. 성공 재생(onLoadedData) 마다 재시도 예산을 복구해 반복 만료도 매번 회복한다.
 *   재발급 후에도 실패(미지원 코덱 등)면 하드 실패 → fallback.
 * - 자동재생 안 함(controls, preload=metadata). 셀룰러 데이터·다중 첨부 성능 배려.
 */

interface MediaProps {
  attachment: MessageAttachment;
  /** 하드 실패(미지원 코덱·재발급 후에도 로드 실패) 시 강등 렌더. 보통 <AttachmentFile>. */
  fallback: ReactNode;
}

/**
 * presigned inline URL 발급 + 재발급(reload). 각 호출은 fresh 서명 URL(만료 복구).
 *
 * 2026-08-14 — 워크스페이스 문서 패널의 동영상·오디오 미리보기가 그대로 쓴다(export 만 추가).
 * 여기 로직이 곧 **TTL 5분 만료 복구 규약**이라, 복제하면 한쪽만 고쳐지는 사고가 난다.
 */
export function useMediaUrl(fileId: string): { url: string | null; failed: boolean; reload: () => void } {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const reqId = useRef(0);

  const reload = useCallback(() => {
    const my = ++reqId.current;
    requestPresignedDownload(fileId, { disposition: 'inline', purpose: 'preview' }) // 인라인 재생 = 「열람」(§3.4.1)
      .then((res) => {
        if (my === reqId.current) {
          setUrl(res.downloadUrl);
          setFailed(false);
        }
      })
      .catch(() => {
        if (my === reqId.current) setFailed(true);
      });
  }, [fileId]);

  useEffect(() => {
    reload();
    return () => {
      // unmount/재요청 시 in-flight 응답 무시.
      reqId.current++;
    };
  }, [reload]);

  return { url, failed, reload };
}

function LoadingCard({ icon, name }: { icon: ReactNode; name: string }) {
  return (
    <div className="chat-attachment-media chat-attachment-media--loading" aria-busy="true" aria-label={name}>
      {icon}
      <span className="chat-attachment-name">{name}</span>
    </div>
  );
}

/** 미디어 error → 1회 재발급, 성공 재생 시 재시도 예산 복구, 재발급 후 실패는 하드 실패. */
export function useMediaRetry(reload: () => void) {
  const [hardFailed, setHardFailed] = useState(false);
  const retried = useRef(false);
  const onError = useCallback(() => {
    if (!retried.current) {
      retried.current = true;
      reload();
    } else {
      setHardFailed(true);
    }
  }, [reload]);
  const onLoadedData = useCallback(() => {
    retried.current = false;
  }, []);
  return { hardFailed, onError, onLoadedData };
}

export function AttachmentAudio({ attachment, fallback }: MediaProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const { url, failed, reload } = useMediaUrl(attachment.fileId);
  const { hardFailed, onError, onLoadedData } = useMediaRetry(reload);
  const name = attachment.name ?? tShared('shared.attach.file_fallback');

  if (failed || hardFailed) return <>{fallback}</>;
  if (!url) return <LoadingCard icon={<Mic size={14} />} name={name} />;

  return (
    <figure className="chat-attachment-media chat-attachment-audio">
      <figcaption className="chat-attachment-media-title" title={attachment.name ?? undefined}>
        <Mic size={13} aria-hidden />
        <span className="chat-attachment-name">{name}</span>
      </figcaption>
      <audio
        className="chat-attachment-audio-el"
        controls
        preload="metadata"
        src={url}
        onError={onError}
        onLoadedData={onLoadedData}
        aria-label={name}
      />
    </figure>
  );
}

export function AttachmentVideo({ attachment, fallback }: MediaProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const { url, failed, reload } = useMediaUrl(attachment.fileId);
  const { hardFailed, onError, onLoadedData } = useMediaRetry(reload);
  const name = attachment.name ?? tShared('shared.attach.file_fallback');

  if (failed || hardFailed) return <>{fallback}</>;
  if (!url) return <LoadingCard icon={<VideoIcon size={14} />} name={name} />;

  return (
    <div className="chat-attachment-media chat-attachment-video">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption -- 사용자 업로드 미디어, 캡션 트랙 없음(Phase 3) */}
      <video
        className="chat-attachment-video-el"
        controls
        preload="metadata"
        playsInline
        src={url}
        onError={onError}
        onLoadedData={onLoadedData}
        aria-label={name}
      />
    </div>
  );
}
